import { EffectiveSettingsSchema, type EffectiveSettingsOutput } from '@dawn/feature-config';
import { queryOptions, useQuery } from '@tanstack/react-query';

import { settingsApi } from '@/lib/api';

import { queryKeys } from './query-keys';

export type EffectiveSettings = EffectiveSettingsOutput;

/**
 * SETTINGS_DEFAULTS — the canonical "no override" snapshot. Built once by
 * `EffectiveSettingsSchema.parse({})` so every default (sidebar.isOpen=true,
 * theme='light', etc.) is sourced from the Zod schema rather than duplicated
 * here. Frozen to prevent accidental mutation of the shared placeholder
 * reference (TanStack `placeholderData` returns the same object identity).
 *
 * (TDEV-266 Phase 5.1 — widened from `{ theme: 'light' }`.)
 */
export const SETTINGS_DEFAULTS: EffectiveSettings = Object.freeze(
  EffectiveSettingsSchema.parse({})
) as EffectiveSettings;

/**
 * Settings query options.
 *
 * - `staleTime: 30_000` — keep data fresh for 30 s before background refetch.
 *   Overrides the global QueryClient default (0).
 * - `refetchOnWindowFocus: true` — pull a fresh snapshot when the user
 *   refocuses the tab, so cross-tab settings edits become visible.
 *   Overrides the global QueryClient default (false).
 * - `placeholderData` — serve `SETTINGS_DEFAULTS` while the first fetch is in
 *   flight so consumers can read `data.sidebar.isOpen` synchronously.
 *
 * (TDEV-266 Phase 5.1 plan.md:1098-1140.)
 */
export const settingsQueryOptions = queryOptions({
  queryKey: queryKeys.settings.root,
  queryFn: () => settingsApi.getSettings(),
  staleTime: 30_000,
  refetchOnWindowFocus: true,
  placeholderData: SETTINGS_DEFAULTS,
});

export function useSettingsQuery() {
  return useQuery(settingsQueryOptions);
}
